import React, { ComponentType, ReactNode, useEffect, useReducer } from "react";

type TPageModule = { default: ComponentType<any> };

export interface ILazyPage extends React.FC<any> {
    /** The page module's path from the project root, which is the key the build manifest lists its assets under. */
    moduleId: string;
    /** Loads the page module. Resolves straight away once it is in memory. */
    preload: () => Promise<void>;
}

/**
 * Loads a page module on demand. This replaced @loadable/component.
 *
 * Neither obvious alternative works here. React Router's `lazy` route property is
 * only resolved by the data routers, and this app renders through useRoutes, which
 * ignores it. React.lazy suspends for at least a microtask even when the module is
 * already in memory, and renderToString does not wait for a suspended boundary - the
 * server would send the fallback instead of the page.
 *
 * So loading is explicit: the server awaits preload() for the routes it matched
 * before it renders, and the client awaits the same ones before it hydrates, which
 * is the job loadableReady() used to do. The fallback below is only ever reached on
 * a client side navigation to a page that has not been loaded yet.
 */
/**
 * Lazy pages that rendered their fallback during a server render, because nothing
 * preloaded them. Not every lazy module sits on a route - some are rendered inside a
 * page - and the server cannot know about those before it has rendered once. The
 * renderer drains this, awaits them and renders again.
 */
const renderedUnloaded = new Set<ILazyPage>();

/**
 * Every lazy page by its module id, so the browser can be told which ones the server
 * rendered. The ones sitting on the matched route it can work out for itself; the ones
 * rendered inside a page it cannot, and rendering a fallback where the server sent the
 * real thing is a hydration mismatch.
 */
const byModuleId = new Map<string, ILazyPage>();

/**
 * Every lazy page a server render put on the screen, loaded or not. The unloaded set
 * below cannot answer this: a page fetched for an earlier request stays loaded for the
 * life of the process, so from the second request on it renders without ever announcing
 * itself - while the browser, which starts from nothing every time, still has to fetch
 * it before it can hydrate.
 */
const renderedPages = new Set<ILazyPage>();

export const collectRenderedPages = (): ILazyPage[] => {
    const pages = Array.from(renderedPages);
    renderedPages.clear();
    return pages;
};

export const preloadPages = async (moduleIds: string[]): Promise<void> => {
    // In passes, for the reason the server renders in passes: a lazy page can hold
    // another one, and the inner one only registers itself once the module that declares
    // it has been loaded. Ids that never turn up belong to pages this bundle does not
    // have, and nothing on this render is waiting for them.
    const remaining = new Set(moduleIds);
    for (let pass = 0; pass < 3 && remaining.size; pass++) {
        const pages: ILazyPage[] = [];
        for (const id of remaining) {
            const page = byModuleId.get(id);
            if (page) {
                pages.push(page);
                remaining.delete(id);
            }
        }
        if (!pages.length) return;
        await Promise.all(pages.map(page => page.preload().catch(() => undefined)));
    }
};

export const collectUnloadedPages = (): ILazyPage[] => {
    const pages = Array.from(renderedUnloaded);
    renderedUnloaded.clear();
    return pages;
};

export const lazyPage = (
    moduleId: string,
    load: () => Promise<TPageModule>,
    fallback: ReactNode = null
): ILazyPage => {
    let Loaded: ComponentType<any> | undefined;
    let pending: Promise<void> | undefined;

    const preload = () => {
        if (Loaded) {
            return Promise.resolve();
        }
        if (!pending) {
            pending = load().then(
                pageModule => {
                    Loaded = pageModule.default;
                },
                error => {
                    // Forgotten so a later attempt can try the network again rather
                    // than replaying the same rejection forever.
                    pending = undefined;
                    throw error;
                }
            );
        }
        return pending;
    };

    const LazyPage = ((props: any) => {
        const [, rerender] = useReducer((count: number) => count + 1, 0);
        useEffect(() => {
            if (Loaded) {
                return;
            }
            let active = true;
            preload().then(() => {
                if (active) {
                    rerender();
                }
            });
            return () => {
                active = false;
            };
        }, []);
        if (typeof window === "undefined") {
            renderedPages.add(LazyPage);
            if (!Loaded) {
                renderedUnloaded.add(LazyPage);
            }
        }
        return Loaded ? <Loaded {...props} /> : <>{fallback}</>;
    }) as ILazyPage;

    LazyPage.displayName = `LazyPage(${moduleId})`;
    LazyPage.moduleId = moduleId;
    LazyPage.preload = preload;

    byModuleId.set(moduleId, LazyPage);

    return LazyPage;
};

export default lazyPage;
